import {isCopilot} from '@github-ui/assignees/copilot-user'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {NullMessageId, type TextReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {getSelectedThread} from '@github-ui/copilot-chat/utils/get-selected-thread'
import {sendEvent} from '@github-ui/hydro-analytics'
import {dump} from 'js-yaml'
import {useCallback} from 'react'

import type {IssueTreeItem} from '../../../markdown-extensions/issue-blocks/issue-tree/IssueTreeBlock'
import type {TimelineEventTextReference} from '../../timeline-events/timeline-events-types'
import {useContentPreview} from '../ContentPreviewContext'

export type ContinuableError = {
  message: string
  type: 'project' | 'dependency' | 'update' | 'other'
}

const ERROR_TYPE_DESCRIPTION: Record<ContinuableError['type'], string> = {
  project: 'Project assignment errors',
  dependency: 'Dependency errors',
  update: 'Partial update errors',
  other: 'Other errors',
}

export type SavedIssue = {
  draftType: 'new-issue' | 'update-issue' | 'existing-issue'
  tag: string
  parentTag: string | undefined
  blockedByTags: string[] | undefined
  isBlockingTags: string[] | undefined
  template: string | undefined
  repository: {
    databaseId: number
    name: string
    owner: {
      login: string
    }
  }
  number: number
  title: string
  url: string
  assignees: string[]
  errors?: ContinuableError[]
}

export function useOnSaveCallback() {
  const manager = useChatManager()
  const state = useChatState()
  const {closeItems, openItem, openPreviewPane, versionedItems, updateItem} = useContentPreview()

  return useCallback(
    async (savedIssues: SavedIssue[]) => {
      if (savedIssues.length === 0) {
        return
      }

      const primarySavedIssue = savedIssues.find(issue => !issue.parentTag) ?? savedIssues[0]!

      sendEvent('dotcom_chat.activate', {
        target: 'BROWSER_ISSUES_SAVED',
        mode: 'immersive',
        threadId: getSelectedThread(state)?.id,
        chatReferrer: document.referrer,
        repositoryId: primarySavedIssue.repository.databaseId,
        draftType: primarySavedIssue.draftType,
        issueTag: primarySavedIssue.tag,
        issueNumber: primarySavedIssue.number,
        templateType: primarySavedIssue.template
          ? primarySavedIssue.template.endsWith('.md')
            ? 'markdown'
            : 'yaml'
          : 'none',
        issuesWithCopilotAssigned: savedIssues.filter(issue => issue.assignees.some(s => isCopilot(s))).length,
        savedIssuesCount: savedIssues.length,
      })

      const savedIssuesUrls: string[] = []
      const savedIssuesForYaml: IssueTreeItem[] = []
      const obsoleteDraftTags: string[] = []

      for (const savedIssue of savedIssues) {
        const owner = savedIssue.repository.owner.login
        const repo = savedIssue.repository.name
        const number = savedIssue.number

        // Add saved issue in the content preview context
        // This happens first so that we don't have to wait for the API call to complete
        // to show the issue in the preview pane
        updateItem({
          type: 'issue',
          id: getIssuePreviewId(savedIssue),
          messageId: NullMessageId,
          name: savedIssue.title,
          owner,
          repo,
          number,
          href: savedIssue.url,
        })

        const parentIssue = savedIssues.find(issue => issue.tag === savedIssue.parentTag)
        const blockedByIssues = savedIssue.blockedByTags
          ?.map(tag => savedIssues.find(issue => issue.tag === tag))
          .filter(issue => issue != null)
        const isBlockingIssues = savedIssue.isBlockingTags
          ?.map(tag => savedIssues.find(issue => issue.tag === tag))
          .filter(issue => issue != null)

        const blockedByTags = blockedByIssues?.length ? blockedByIssues.map(issue => getIssueTag(issue)) : undefined
        const isBlockingTags = isBlockingIssues?.length ? isBlockingIssues.map(issue => getIssueTag(issue)) : undefined

        savedIssuesForYaml.push({
          tag: `${owner}/${repo}#${number}`,
          parentTag: parentIssue ? getIssueTag(parentIssue) : undefined,
          blockedByTags,
          isBlockingTags,
          title: savedIssue.title,
          repository: `${savedIssue.repository.owner.login}/${savedIssue.repository.name}`,
          number: savedIssue.number,
          state: 'open',
          url: savedIssue.url,
        })
        savedIssuesUrls.push(getIssueUrl(savedIssue))
        obsoleteDraftTags.push(savedIssue.tag)
      }

      // Since the issues have been saved, we no longer need the draft open.
      // We don't know which draft versions may be open, so we close them all.
      // This updates context state, so needs to be done in one call outside the loop above.
      closeItems(
        savedIssues
          .map(issue => `${issue.draftType}:${issue.tag}` as const)
          .flatMap(unversionedDraftId => versionedItems.get(unversionedDraftId) ?? []),
      )

      // Open only the root/primary issue in the preview pane
      openItem(getIssuePreviewId(primarySavedIssue))
      openPreviewPane()

      const instructions: TimelineEventTextReference = {
        type: 'text',
        name: `timeline-event: {"type": "issue-saved", "markdownContent": "${instructionsMarkdownContent(
          savedIssues,
        )}"}`,
        text:
          `${issueFetchInstructions(savedIssues)}\n` +
          '````yaml type="issue-tree"\n' +
          `${dump(
            {data: savedIssuesForYaml},
            {
              forceQuotes: true,
              noArrayIndent: true,
              lineWidth: -1,
            },
          )}` +
          '````',
      }

      const ignoreObsoleteDraftsInstruction: TextReference = {
        type: 'text',
        name: 'ignore-obsolete-drafts',
        text: `From now on, you should only interact with these newly saved issues and not their corresponding drafts. You MUST exclude the draft issues with the following tags from any future tree creation: ${obsoleteDraftTags.join(
          ', ',
        )}`,
      }

      const references = [
        // Instructions on how to return the newly-saved issues
        instructions,
        // Instructions on how to ignore obsolete drafts
        ignoreObsoleteDraftsInstruction,
        // Any unsent draft edits, so Copilot keeps an updated context of the user's actions
        ...state.currentReferences,
      ]

      const continuableErrors: ContinuableError[] = savedIssues.flatMap(issue => issue.errors || [])

      if (continuableErrors.length) {
        references.push({
          type: 'text',
          name: 'summarize-continuable-errors',
          text: `These issues were saved with some errors. You MUST give a high-level summary of these errors at the end of your message in the language used in the conversation. Always prefix it with the word "Note:" in bold for visibility. Be clear but succinct. Here are the errors, grouped by type:\n${groupErrorsByType(
            continuableErrors,
          )}`,
        })
      }

      const content =
        savedIssuesUrls.length > 1
          ? `Saved the issues: ${savedIssuesUrls.join(', ')}`
          : `Saved the issue: ${savedIssuesUrls[0]}`

      await manager.sendChatMessage({
        thread: getSelectedThread(state),
        content,
        references,
        topic: state.currentTopic,
        context: state.context,
        customInstructions: state.customInstructions,
        model: state.model,
      })
    },
    [closeItems, manager, openItem, openPreviewPane, state, updateItem, versionedItems],
  )
}

function groupErrorsByType(errors: ContinuableError[]): string {
  const grouped: Record<string, string[]> = {}
  for (const error of errors) {
    const description = ERROR_TYPE_DESCRIPTION[error.type] || 'Other errors'
    if (!grouped[description]) {
      grouped[description] = []
    }
    grouped[description].push(error.message)
  }

  return Object.entries(grouped)
    .map(([type, messages]) => `${type}:\n${messages.map(m => `- ${m}`).join('\n')}\n`)
    .join('\n')
}

function getIssueUrl(savedIssue: SavedIssue): string {
  return `[${getIssueTag(savedIssue)}](${savedIssue.url})`
}

function getIssueTag(savedIssue: SavedIssue): string {
  const owner = savedIssue.repository.owner.login
  const repo = savedIssue.repository.name
  const number = savedIssue.number
  return `${owner}/${repo}#${number}`
}

function instructionsMarkdownContent(savedIssues: SavedIssue[]) {
  if (savedIssues.length > 1) {
    return `Saved ${savedIssues.length} issues`
  } else {
    const savedIssue = savedIssues[0]!
    const url = getIssueUrl(savedIssue)
    const action = savedIssue.draftType === 'new-issue' ? 'Created' : 'Updated'
    return `${action} issue ${url}`
  }
}

function issueFetchInstructions(savedIssues: SavedIssue[]) {
  if (savedIssues.length > 1) {
    return 'Fetch the details for all saved issues and return them in a code block with YAML following this example format:'
  } else {
    return 'Fetch the issue details and return them in a code block with YAML following this example format:'
  }
}

function getIssuePreviewId(issue: SavedIssue): `issue:${string}/${string}/${number}` {
  const owner = issue.repository.owner.login
  const repo = issue.repository.name
  const number = issue.number
  return `issue:${owner}/${repo}/${number}` as const
}
